import React, { useState } from "react";
import { ListFilter } from "lucide-react";
import { Button } from "components/Graphs/Button";
import { Input } from "components/Graphs/Input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSubMenu,
  DropdownMenuSubMenuContent,
  DropdownMenuSubMenuTrigger,
  DropdownMenuTrigger,
} from "components/Graphs/Dropdown";
import type {
  ActivityFilter,
  NetworkFilter,
} from "./hooks/useOrganizationComputeResourceList";
import type {
  ComputeListFilters,
  ComputeListFilterUpdate,
} from "./hooks/useComputeListFilters";
import type { TeamMember } from "./hooks/useOrganizationTeamMembers";

const filterRadioItemClass =
  "rounded-md py-2 pl-8 pr-2 text-xs text-secondary data-[state=checked]:bg-highlight/10 data-[state=checked]:text-white data-[state=checked]:font-medium [&>span]:left-2 [&>span]:size-3.5 data-[state=checked]:[&>span>svg:first-child]:!text-highlight data-[state=unchecked]:[&>span>svg:last-child]:text-[#525252]";

const quickFilterClass = (selected: boolean) =>
  `h-full !rounded-none border-0 border-r border-zinc-700 px-3 py-0 text-sm font-normal transition-colors last:border-r-0 ${
    selected
      ? "bg-zinc-800 text-white hover:bg-zinc-700"
      : "bg-zinc-900/40 text-secondary hover:bg-zinc-800 hover:text-white"
  }`;

const statusFilterOptions: ReadonlyArray<{
  label: string;
  value: ActivityFilter;
}> = [
  { label: "All", value: "all" },
  { label: "Active", value: "active" },
  { label: "Inactive", value: "inactive" },
];

const networkFilterOptions: ReadonlyArray<{
  label: string;
  value: NetworkFilter;
}> = [
  { label: "All", value: "all" },
  { label: "Meshed", value: "meshed" },
  { label: "Isolated", value: "isolated" },
  { label: "Egress only", value: "egress-only" },
];

const ComputeFilterSubMenu = <Value extends string>({
  label,
  onValueChange,
  options,
  value,
}: {
  label: string;
  onValueChange: (value: Value) => void;
  options: readonly { label: string; value: Value }[];
  value: Value;
}) => (
  <DropdownMenuSubMenu>
    <DropdownMenuSubMenuTrigger>{label}</DropdownMenuSubMenuTrigger>
    <DropdownMenuSubMenuContent>
      <DropdownMenuRadioGroup
        value={value}
        onValueChange={(nextValue) => onValueChange(nextValue as Value)}
      >
        {options.map((option) => (
          <DropdownMenuRadioItem
            key={option.value}
            value={option.value}
            indicator="check"
            className={filterRadioItemClass}
          >
            {option.label}
          </DropdownMenuRadioItem>
        ))}
      </DropdownMenuRadioGroup>
    </DropdownMenuSubMenuContent>
  </DropdownMenuSubMenu>
);

const ComputeFilters: React.FC<{
  members: TeamMember[];
  currentUserId?: string;
  filters: ComputeListFilters;
  setFilters: (filters: ComputeListFilterUpdate) => void;
}> = ({ members, currentUserId, filters, setFilters }) => {
  const [memberSearch, setMemberSearch] = useState("");
  const visibleMembers = members.filter(
    (member) =>
      member.id !== currentUserId &&
      member.name.toLowerCase().includes(memberSearch.trim().toLowerCase()),
  );
  const isCurrentUserCreator = filters.creatorId === currentUserId;
  const isAllOwnershipSelected =
    filters.creatorId === "all" && filters.ownership === "all";
  const isMineOwnershipSelected =
    filters.ownership === "mine" &&
    (filters.creatorId === "all" || isCurrentUserCreator);

  const setCreator = (creatorId: string) => {
    setFilters({
      creatorId,
      ownership: creatorId === currentUserId ? "mine" : "all",
    });
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div
        className="flex h-8 items-center overflow-hidden rounded-md border border-zinc-700 transition-colors hover:border-zinc-500"
        role="group"
        aria-label="Compute type"
      >
        {(["all", "cloud", "local"] as const).map((type) => (
          <Button
            key={type}
            type="button"
            variant="ghost"
            onClick={() => setFilters({ type })}
            aria-pressed={filters.type === type}
            className={quickFilterClass(filters.type === type)}
          >
            {type === "all" ? "All" : type === "cloud" ? "Cloud" : "Local"}
          </Button>
        ))}
      </div>
      <div
        className="flex h-8 items-center overflow-hidden rounded-md border border-zinc-700 transition-colors hover:border-zinc-500"
        role="group"
        aria-label="Compute ownership"
      >
        {(["all", "mine"] as const).map((ownership) => {
          const selected =
            ownership === "all"
              ? isAllOwnershipSelected
              : isMineOwnershipSelected;
          return (
            <Button
              key={ownership}
              type="button"
              variant="ghost"
              onClick={() => setFilters({ ownership, creatorId: "all" })}
              aria-pressed={selected}
              className={quickFilterClass(selected)}
            >
              {ownership === "all" ? "All" : "Mine"}
            </Button>
          );
        })}
      </div>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="secondary"
            className="h-8 gap-2 border-zinc-700 bg-zinc-900/40 px-3 py-0 text-sm font-normal text-secondary hover:border-zinc-500 hover:bg-zinc-800 hover:text-white"
          >
            <ListFilter className="h-4 w-4" strokeWidth={1.75} />
            Filter
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          <DropdownMenuSubMenu>
            <DropdownMenuSubMenuTrigger>Creator</DropdownMenuSubMenuTrigger>
            <DropdownMenuSubMenuContent className="w-64 p-2">
              <Input
                type="search"
                value={memberSearch}
                onChange={(event) => setMemberSearch(event.target.value)}
                onKeyDown={(event) => event.stopPropagation()}
                placeholder="Search team members"
                className="mb-2"
                inputClassName="border-zinc-700 bg-primaryBg py-1.5 text-sm text-primary placeholder:text-secondary"
              />
              <div className="max-h-64 overflow-y-auto">
                <DropdownMenuRadioGroup
                  value={
                    filters.ownership === "mine" && currentUserId
                      ? currentUserId
                      : filters.creatorId
                  }
                  onValueChange={setCreator}
                >
                  <DropdownMenuRadioItem
                    value="all"
                    indicator="check"
                    className={filterRadioItemClass}
                  >
                    All creators
                  </DropdownMenuRadioItem>
                  {currentUserId ? (
                    <DropdownMenuRadioItem
                      value={currentUserId}
                      indicator="check"
                      className={filterRadioItemClass}
                    >
                      Me
                    </DropdownMenuRadioItem>
                  ) : null}
                  {visibleMembers.map((member) => (
                    <DropdownMenuRadioItem
                      key={member.id}
                      value={member.id}
                      indicator="check"
                      className={filterRadioItemClass}
                    >
                      {member.name}
                    </DropdownMenuRadioItem>
                  ))}
                </DropdownMenuRadioGroup>
              </div>
            </DropdownMenuSubMenuContent>
          </DropdownMenuSubMenu>
          <ComputeFilterSubMenu
            label="Status"
            onValueChange={(status) => setFilters({ status })}
            options={statusFilterOptions}
            value={filters.status}
          />
          <ComputeFilterSubMenu
            label="Network"
            onValueChange={(network) => setFilters({ network })}
            options={networkFilterOptions}
            value={filters.network}
          />
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export default ComputeFilters;
